import { useCallback } from 'react';
import { Editor, Range } from 'slate';
import { useCreateBlockChain } from 'app/api/tag-queries/tags';
import { applyBlockTagBinding } from 'app/slate/extensions/tag/utils/blockTagging';
import { collectChainCarriers, bindingsFromResponse } from 'app/slate/extensions/tag/utils/blockChain';

export interface ApplyBlockChainArgs {
  editor: Editor;
  range: Range;
  base: {
    documentSuperId: number;
    taxonomyId: string;
    sectionSuperId: string;
    pageId: string;
    conceptId: string;
    lang: string;
    contextId?: string;
    linkroleUri?: string | null;
  };
  conceptTagIds?: Set<string>;
  predecessorId?: string;
  successorId?: string;
  documentVersionId?: string;
}

export const useApplyBlockChain = () => {
  const { mutateAsync: createBlockChain } = useCreateBlockChain();

  return useCallback(
    async (args: ApplyBlockChainArgs): Promise<{ createdCount: number }> => {
      const { editor, range, base, conceptTagIds, predecessorId, successorId, documentVersionId } = args;
      const carriers = collectChainCarriers(editor, range, { conceptTagIds });
      if (carriers.length === 0) return { createdCount: 0 };

      const created = await createBlockChain({
        ...base,
        carrierElementIds: carriers.map((c) => c.elementId),
        activeSectionSuperId: base.sectionSuperId,
        predecessorId,
        successorId,
        documentVersionId,
      });
      if (!created?.length) return { createdCount: 0 };

      Editor.withoutNormalizing(editor, () => {
        bindingsFromResponse(carriers, created, successorId).forEach(({ path, binding }) => applyBlockTagBinding(editor, path, binding));
      });
      return { createdCount: created.length };
    },
    [createBlockChain]
  );
};
